import { useEffect, useMemo, useState } from 'react';
import { Bell, Download, Droplets, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { formatTime } from '@/lib/utils';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function isStandaloneApp() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

export function PWAInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('flexiplanner-pwa-dismissed') === '1');
  const settings = useUserStore(s => s.settings);
  const updateSettings = useUserStore(s => s.updateSettings);
  const tasks = useTasksStore(s => s.tasks);

  const nextActivity = useMemo(() => {
    const now = Date.now();
    return tasks
      .filter(task => {
        if (!task.scheduledStart) return false;
        if (task.status === 'done' || task.status === 'completed' || task.status === 'cancelled') return false;
        return new Date(task.scheduledStart).getTime() > now;
      })
      .sort((a, b) => new Date(a.scheduledStart!).getTime() - new Date(b.scheduledStart!).getTime())[0];
  }, [tasks]);

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!settings.enableNotifications || !('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const timers: number[] = [];
    if (nextActivity?.scheduledStart) {
      const start = new Date(nextActivity.scheduledStart);
      const notifyAt = start.getTime() - 5 * 60 * 1000;
      const delay = Math.max(1000, notifyAt - Date.now());
      if (delay < 24 * 60 * 60 * 1000) {
        timers.push(
          window.setTimeout(() => {
            new Notification(`Next: ${nextActivity.title}`, {
              body: `${formatTime(start)}. Tap back into Fluxiplanner to start or move it.`,
              icon: '/pwa-192.png',
              badge: '/pwa-192.png',
            });
          }, delay)
        );
      }
    }

    timers.push(
      window.setInterval(() => {
        new Notification('Hydration check', {
          body: 'Log water and current energy when you have a second.',
          icon: '/pwa-192.png',
          badge: '/pwa-192.png',
        });
      }, 2 * 60 * 60 * 1000)
    );

    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [nextActivity, settings.enableNotifications]);

  if (dismissed || isStandaloneApp()) return null;

  const notificationAvailable = 'Notification' in window;
  const needsNotificationPermission =
    settings.enableNotifications && notificationAvailable && Notification.permission === 'default';

  if (!installPrompt && !needsNotificationPermission) return null;

  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  async function enableNotifications() {
    if (!notificationAvailable) return;
    const permission = await Notification.requestPermission();
    if (permission === 'granted') updateSettings({ enableNotifications: true });
  }

  function dismiss() {
    localStorage.setItem('flexiplanner-pwa-dismissed', '1');
    setDismissed(true);
  }

  return (
    <div className="fixed inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-50 mx-auto max-w-md rounded-lg border bg-background/94 p-3 shadow-2xl backdrop-blur-xl md:bottom-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Smartphone className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Make Fluxiplanner feel native</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Install the PWA and enable reminders for next activities and hydration checks.
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {installPrompt && (
              <Button size="sm" onClick={installApp}>
                <Download className="mr-2 h-4 w-4" />
                Install
              </Button>
            )}
            {needsNotificationPermission && (
              <Button size="sm" variant="outline" onClick={enableNotifications}>
                <Bell className="mr-2 h-4 w-4" />
                Reminders
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss}>
              Later
            </Button>
          </div>
        </div>
        <Droplets className="mt-1 h-4 w-4 shrink-0 text-cyan-500" aria-hidden="true" />
      </div>
    </div>
  );
}
